import React, { useState, useRef } from 'react';
import { User, Calendar, MapPin, Zap, Activity, ArrowLeft, ArrowRight, Check, Camera } from 'lucide-react';
import styles from './OnboardingPage.module.css';

const INJURY_TYPES = [
  'Stress Fracture',
  'Iliotibial band syndrome',
  'Plantar fasciitis',
  "Runner's knee",
  'Shin splints',
  'Ankle sprain',
  'Hamstring strain',
  'ACL injury',
  'Other',
];

const FRACTURE_LOCATIONS = [
  'Tibia (Shinbone)',
  'Metatarsals (Foot Bones)',
  'Navicular (Midfoot)',
  'Fibula (Outer Lower Leg)',
  'Calcaneus (Heel Bone)',
  'Femoral Neck (Hip)',
  'Other',
];

const SPORTS = [
  'Running',
  'Football',
  'Basketball',
  'Tennis',
  'Dance',
  'Gym / Weightlifting',
  'Other',
];

const RECOVERY_STAGES = [
  { name: 'Diagnosis', color: '#D8A7A0', description: 'Just diagnosed, awaiting imaging or results' },
  { name: 'Protection', color: '#E6D3A3', description: 'Resting and protecting the injury site' },
  { name: 'Rehab', color: '#A8C3A0', description: 'Active rehabilitation and strengthening' },
  { name: 'Return to Sport', color: '#2F6F4F', description: 'Gradually returning to full activity' },
];

function getStepKeys(injuryType: string): string[] {
  const keys = ['name', 'date', 'injury'];
  if (injuryType === 'Stress Fracture') keys.push('location');
  keys.push('sport', 'stage');
  return keys;
}

function formatDateLabel(d: Date): string {
  const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks === 1) return '1 week ago';
  if (diffDays < 30) return `${weeks} weeks ago`;
  const months = Math.floor(diffDays / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

function generateDateOptions(): Date[] {
  const options: Date[] = [];
  const now = new Date();
  for (let i = 0; i < 180; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    options.push(d);
  }
  return options;
}

interface OnboardingPageProps {
  onComplete: (data: {
    userName: string;
    profilePhoto: string | null;
    recoveryStartDate: string;
    injuryType: string;
    fractureLocation: string;
    injurySport: string;
    initialStage: number;
  }) => Promise<void>;
}

export default function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [userName, setUserName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [recoveryStartDate, setRecoveryStartDate] = useState(new Date());
  const [injuryType, setInjuryType] = useState('');
  const [fractureLocation, setFractureLocation] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [injurySport, setInjurySport] = useState('');
  const [initialStage, setInitialStage] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dateOptions = generateDateOptions();

  const stepKeys = getStepKeys(injuryType);
  const TOTAL_STEPS = stepKeys.length;
  const currentKey = stepKeys[step];

  const getFinalLocation = () => {
    if (fractureLocation === 'Other' && customLocation.trim()) return customLocation.trim();
    return fractureLocation;
  };

  const canAdvance = (): boolean => {
    if (currentKey === 'name') return userName.trim().length > 0;
    if (currentKey === 'date') return true;
    if (currentKey === 'injury') return injuryType.length > 0;
    if (currentKey === 'location') return fractureLocation.length > 0 && (fractureLocation !== 'Other' || customLocation.trim().length > 0);
    if (currentKey === 'sport') return injurySport.length > 0;
    if (currentKey === 'stage') return initialStage !== null;
    return false;
  };

  const handleNext = async () => {
    if (!canAdvance()) return;
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
    } else {
      setSaving(true);
      try {
        await onComplete({
          userName: userName.trim(),
          profilePhoto,
          recoveryStartDate: recoveryStartDate.toISOString(),
          injuryType,
          fractureLocation: injuryType === 'Stress Fracture' ? getFinalLocation() : '',
          injurySport,
          initialStage: initialStage ?? 0,
        });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setProfilePhoto(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const isLast = step === TOTAL_STEPS - 1;

  return (
    <div className={styles.page}>
      <div className={styles.bg} />

      <div className={styles.card}>
        <div className={styles.logoRow}>
          <span className={styles.logo}>beryl</span>
          <span className={styles.tagline}>Your Recovery Buddy</span>
        </div>

        {/* Step dots */}
        <div className={styles.dots}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={styles.dot}
              style={{
                width: i === step ? 28 : 10,
                background: i === step ? '#B12612' : i < step ? '#D98A63' : 'rgba(90,58,46,0.2)',
              }}
            />
          ))}
        </div>

        {/* Step: Name + photo */}
        {currentKey === 'name' && (
          <div className={styles.stepContent}>
            <div className={styles.avatarWrap}>
              <button className={styles.avatarBtn} onClick={() => fileInputRef.current?.click()}>
                {profilePhoto ? (
                  <img src={profilePhoto} alt="Profile" className={styles.avatarImg} />
                ) : (
                  <div className={styles.avatarPlaceholder}>
                    <User size={44} color="#B12612" />
                  </div>
                )}
                <div className={styles.avatarBadge}>
                  <Camera size={13} color="#fff" />
                </div>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
              {profilePhoto && (
                <button className={styles.removePhoto} onClick={() => setProfilePhoto(null)}>Remove photo</button>
              )}
            </div>

            <h2 className={styles.stepTitle}>Welcome to beryl</h2>
            <p className={styles.stepSub}>Let's start with the basics — what should we call you?</p>

            <div className={styles.nameRow}>
              <User size={18} color="#B12612" style={{ flexShrink: 0 }} />
              <input
                className={styles.nameInput}
                value={userName}
                onChange={e => setUserName(e.target.value)}
                placeholder="Your preferred name"
                onKeyDown={e => { if (e.key === 'Enter' && canAdvance()) handleNext(); }}
                autoFocus
              />
            </div>
            {!profilePhoto && (
              <p className={styles.hint}>Photo is optional — tap the circle above to add one</p>
            )}
          </div>
        )}

        {/* Step: Recovery date */}
        {currentKey === 'date' && (
          <div className={styles.stepContent}>
            <div className={styles.iconCircle} style={{ background: 'rgba(177,38,18,0.1)' }}>
              <Calendar size={38} color="#B12612" />
            </div>
            <h2 className={styles.stepTitle}>When did your recovery start?</h2>
            <p className={styles.stepSub}>This helps us track your progress over time</p>

            <div className={styles.dateDisplay}>
              <Calendar size={16} color="#B12612" />
              <span className={styles.dateDisplayText}>
                {recoveryStartDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className={styles.dateDisplaySub}>{formatDateLabel(recoveryStartDate)}</span>
            </div>

            <div className={styles.dateList}>
              {dateOptions.map(d => {
                const isSelected = d.toDateString() === recoveryStartDate.toDateString();
                return (
                  <button
                    key={d.toISOString()}
                    className={`${styles.dateOption} ${isSelected ? styles.dateOptionActive : ''}`}
                    onClick={() => setRecoveryStartDate(d)}
                  >
                    <span style={isSelected ? { color: '#B12612', fontWeight: 700 } : { color: '#5A3A2E' }}>
                      {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span style={isSelected ? { color: '#B12612' } : { color: '#8A6358', fontSize: 13 }}>
                      {formatDateLabel(d)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step: Injury type */}
        {currentKey === 'injury' && (
          <div className={styles.stepContent}>
            <div className={styles.iconCircle} style={{ background: 'rgba(177,38,18,0.1)' }}>
              <Activity size={38} color="#B12612" />
            </div>
            <h2 className={styles.stepTitle}>What is your injury?</h2>
            <p className={styles.stepSub}>Select the injury you are recovering from</p>

            <div className={styles.chips}>
              {INJURY_TYPES.map(type => (
                <button
                  key={type}
                  className={`${styles.chip} ${injuryType === type ? styles.chipActive : ''}`}
                  onClick={() => {
                    setInjuryType(type);
                    if (type !== 'Stress Fracture') {
                      setFractureLocation('');
                      setCustomLocation('');
                    }
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step: Fracture location (only for Stress Fracture) */}
        {currentKey === 'location' && (
          <div className={styles.stepContent}>
            <div className={styles.iconCircle} style={{ background: 'rgba(217,138,99,0.15)' }}>
              <MapPin size={38} color="#D98A63" />
            </div>
            <h2 className={styles.stepTitle}>Where is your stress fracture?</h2>
            <p className={styles.stepSub}>Select the location of your injury</p>

            <div className={styles.chips}>
              {FRACTURE_LOCATIONS.map(loc => (
                <button
                  key={loc}
                  className={`${styles.chip} ${fractureLocation === loc ? styles.chipActive : ''}`}
                  onClick={() => setFractureLocation(loc)}
                >
                  {loc}
                </button>
              ))}
            </div>
            {fractureLocation === 'Other' && (
              <input
                className={styles.customInput}
                value={customLocation}
                onChange={e => setCustomLocation(e.target.value)}
                placeholder="Enter your fracture location..."
                autoFocus
              />
            )}
          </div>
        )}

        {/* Step: Sport */}
        {currentKey === 'sport' && (
          <div className={styles.stepContent}>
            <div className={styles.iconCircle} style={{ background: 'rgba(217,138,99,0.15)' }}>
              <Zap size={38} color="#D98A63" />
            </div>
            <h2 className={styles.stepTitle}>What caused this injury?</h2>
            <p className={styles.stepSub}>Helps us tailor your recovery plan</p>

            <div className={styles.chips}>
              {SPORTS.map(sport => (
                <button
                  key={sport}
                  className={`${styles.chip} ${injurySport === sport ? styles.chipActive : ''}`}
                  onClick={() => setInjurySport(sport)}
                >
                  {sport}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step: Current stage */}
        {currentKey === 'stage' && (
          <div className={styles.stepContent}>
            <div className={styles.iconCircle} style={{ background: 'rgba(177,38,18,0.1)' }}>
              <Activity size={38} color="#B12612" />
            </div>
            <h2 className={styles.stepTitle}>Where are you in your recovery?</h2>
            <p className={styles.stepSub}>You can update this anytime from the home screen</p>

            <div className={styles.stageList}>
              {RECOVERY_STAGES.map((stage, i) => {
                const isSelected = initialStage === i;
                return (
                  <button
                    key={stage.name}
                    className={styles.stageCard}
                    style={{
                      background: isSelected ? stage.color + '18' : 'rgba(90,58,46,0.04)',
                      borderColor: isSelected ? stage.color : 'transparent',
                    }}
                    onClick={() => setInitialStage(i)}
                  >
                    <div className={styles.stageIcon} style={{ background: isSelected ? stage.color + '25' : 'rgba(90,58,46,0.08)' }}>
                      <Activity size={20} color={isSelected ? stage.color : 'rgba(90,58,46,0.4)'} />
                    </div>
                    <div className={styles.stageText}>
                      <span className={styles.stageName} style={isSelected ? { color: stage.color } : undefined}>{stage.name}</span>
                      <span className={styles.stageDesc}>{stage.description}</span>
                    </div>
                    {isSelected && (
                      <div className={styles.stageCheck} style={{ background: stage.color }}>
                        <Check size={13} color="#fff" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className={styles.navRow}>
          {step > 0 ? (
            <button className={styles.backBtn} onClick={handleBack}>
              <ArrowLeft size={17} /> Back
            </button>
          ) : <div />}
          <button
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={!canAdvance() || saving}
            style={!canAdvance() || saving ? { background: '#DCC9B8', cursor: 'not-allowed', opacity: 0.7 } : undefined}
          >
            {saving ? 'Setting up...' : isLast ? 'Get Started' : 'Next'}
            {!saving && (isLast ? <Check size={17} /> : <ArrowRight size={17} />)}
          </button>
        </div>
      </div>
    </div>
  );
}
